import type { RefRecord } from '@ricoh/ria.client.core/hooks/app/ref.type';
import type { Fetcher } from '@ricoh/ria.client.core/plugins/swr/fetcher';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { useMemo } from 'react';
import { createFetcher } from '@ricoh/ria.client.core/plugins/swr/fetcher';
import { swapProps } from '@ricoh/ria.client.core/utils/data/object';

import type {
  Document,
  FieldMetadataOCRFieldPositions,
  FieldMetadataOCRPosition,
  OCRFieldPositions,
  OCRPosition,
  OCRPositionBox,
  OCRPositionNatifAI,
  Page,
} from '~/common/model/api/batch';
import type { DataType } from '~/common/model/api/common';
import type { IntegrationTypeAI } from '~/common/model/api/integration';
import type { GetOCRPageRequestData } from '~/services/server/api/batch';

import fieldLogic from '~/common/logic/feature/settings/batch-type-library/field';
import { getOCRPageFetcher } from '~/services/server/api/batch';
import { useStoreSelector } from '~/plugins/store/hooks/selector';

import type { DocumentOCRPosition, IsPosition, IsPositions, IsPositionsGeneral } from './_position.type';

import { getOriginalPageId } from '../_page';

/**
 * Check type of Position.
 *
 * @template TType Integration Type Connector.
 * @template TPosition Position type.
 * @param {OCRPosition | FieldMetadataOCRPosition} position Position to check.
 * @param {TType} type Integration type.
 *
 * @returns {boolean} True if match, false otherwise.
 */
export function isPosition<TType extends IntegrationTypeAI, TPosition extends IsPosition<TType>>(
  position: OCRPosition | FieldMetadataOCRPosition,
  type: TType,
): position is TPosition {
  const check: Record<IntegrationTypeAI, Func<[], boolean>> = {
    natifAI: () => 'boxes' in position,
    azureAI: () => 'regions' in position,
  };

  return check[type]();
}

/**
 * Check if positions is Positions General type.
 *
 * @template TType Positions type.
 * @param {DataType} type Field DataType.
 * @param {OCRFieldPositions | FieldMetadataOCRFieldPositions} positions Positions to check.
 *
 * @returns {boolean} Positions is Positions General type.
 */
export function isPositionsGeneral<TType extends OCRFieldPositions | FieldMetadataOCRFieldPositions>(
  type: DataType,
  positions: TType | IsPositions<TType>,
): positions is IsPositionsGeneral<TType> {
  const [position] = positions;
  if (!position) {
    return !fieldLogic.isDataType.table(type);
  }

  return 'pageWidth' in position && 'pageHeight' in position;
}

/**
 * Normalize box.
 *
 * @param {OCRPositionBox} box Box to normalize.
 *
 * @returns {OCRPositionBox} Normalized box.
 */
export function normalizeBox(box: OCRPositionBox): OCRPositionBox {
  if (box.x1 > box.x2) {
    swapProps(box, 'x1', 'x2');
  }

  if (box.y1 > box.y2) {
    swapProps(box, 'y1', 'y2');
  }

  return box;
}

/**
 * Rotate OCR Position NatifAI.
 *
 * @param {Page} page Page.
 * @param {OCRPositionNatifAI} position OCR Position NatifAI.
 *
 * @returns {OCRPositionNatifAI} Rotated OCR Position NatifAI.
 */
function rotateOCRPositionNatifAI({ rotation }: Page, position: OCRPositionNatifAI): OCRPositionNatifAI {
  switch (rotation) {
    case 'clockwise': {
      return {
        ...position,
        pageWidth: position.pageHeight,
        pageHeight: position.pageWidth,
        boxes: position.boxes.map((box) => ({
          ...box,
          x1: box.y1,
          x2: box.y2,
          y1: position.pageWidth - box.x2,
          y2: position.pageWidth - box.x1,
        })),
      };
    }

    case 'counterclockwise': {
      return {
        ...position,
        pageWidth: position.pageHeight,
        pageHeight: position.pageWidth,
        boxes: position.boxes.map((box) => ({
          ...box,
          x1: position.pageHeight - box.y2,
          x2: position.pageHeight - box.y1,
          y1: box.x1,
          y2: box.x2,
        })),
      };
    }

    case 'upsideDown': {
      return {
        ...position,
        pageWidth: position.pageWidth,
        pageHeight: position.pageHeight,
        boxes: position.boxes.map((box) => ({
          ...box,
          x1: position.pageWidth - box.x2,
          x2: position.pageWidth - box.x1,
          y1: position.pageHeight - box.y2,
          y2: position.pageHeight - box.y1,
        })),
      };
    }

    case 'noRotation':
    default: {
      return position;
    }
  }
}

/**
 * Rotate OCR Position.
 *
 * @template TPosition OCRPosition type.
 * @param {Page} page Page.
 * @param {OCRPosition} position OCR Position.
 *
 * @returns {OCRPosition} Rotated OCR Position.
 */
export function rotateOCRPosition<TPosition extends OCRPosition>(page: Page, position: TPosition): TPosition {
  if (isPosition(position, 'natifAI')) {
    return rotateOCRPositionNatifAI(page, position) as TPosition;
  }

  return position;
}

/**
 * Use Get Page OCR Position Fetcher.
 *
 * @param {RefRecord<string, OCRPosition | undefined>} ocrPageCacheRef OCR Page Cache Ref.
 *
 * @returns {Fetcher<GetOCRPageRequestData, Nullable<OCRPosition>>} Get Page OCR Position Fetcher.
 */
export function useGetPageOCRPositionFetcher(
  ocrPageCacheRef: RefRecord<string, OCRPosition | undefined>,
): Fetcher<GetOCRPageRequestData, Nullable<OCRPosition>> {
  return useMemo<Fetcher<GetOCRPageRequestData, Nullable<OCRPosition>>>(
    () =>
      createFetcher(getOCRPageFetcher.key, async ({ batchId, pageId }) => {
        if (!Object.hasOwn(ocrPageCacheRef.current, pageId)) {
          ocrPageCacheRef.current[pageId] = await getOCRPageFetcher.handler({
            batchId,
            pageId,
          });
        }

        return ocrPageCacheRef.current[pageId];
      }),
    [ocrPageCacheRef],
  );
}

/**
 * Use GetDocumentOCRPosition By Document Fetcher.
 *
 * @param {Fetcher<GetOCRPageRequestData, Nullable<OCRPosition>>} getPageOCRPositionFetcher Get Page OCR Position Fetcher.
 *
 * @returns {Fetcher<Document, DocumentOCRPosition> | undefined} GetDocumentOCRPosition By Document Fetcher.
 */
export function useGetDocumentOCRPositionByDocumentFetcher(
  getPageOCRPositionFetcher: Fetcher<GetOCRPageRequestData, Nullable<OCRPosition>>,
): Fetcher<Document, DocumentOCRPosition> | undefined {
  const targetBatch = useStoreSelector((state) => state.feature_process.batch);

  return useMemo<Fetcher<Document, DocumentOCRPosition> | undefined>(() => {
    if (!targetBatch.data) {
      return undefined;
    }

    return createFetcher([getOCRPageFetcher.key, targetBatch.data.id], async ({ pages }) => {
      const pageOCRPositions = await Promise.all(
        pages.map(async (page) => {
          if (!targetBatch.data) {
            return undefined;
          }

          const originalPageId = getOriginalPageId(page);
          return getPageOCRPositionFetcher.handler({
            batchId: targetBatch.data.id,
            pageId: originalPageId,
          });
        }),
      );

      return pageOCRPositions.reduce((prev, pageOCRPosition) => {
        if (pageOCRPosition) {
          prev[pageOCRPosition.pageId] = pageOCRPosition;
        }

        return prev;
      }, {} as DocumentOCRPosition);
    });
  }, [getPageOCRPositionFetcher, targetBatch.data]);
}
